import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useProperties } from "@/hooks/useProperties";
import type { Property } from "@/hooks/useProperties";

// --- DICTIONARIES ---
const dict: Record<string, Record<string, string>> = {
  en: {
    title: "DEM Assistant",
    hello: "Welcome to DEM Properties. How can we assist you today?",
    buy: "Buy a property",
    sell: "Sell a property",
    look: "Just looking",
    type_q: "What kind of property are you looking for?",
    villa: "Villa",
    apartment: "Apartment / Flat",
    penthouse: "Penthouse",
    location_q: "Which area do you prefer?",
    loc_marbella: "Marbella & Surroundings",
    loc_estepona: "Estepona",
    loc_other: "Any Area",
    beds_q: "How many bedrooms do you need?",
    beds_1: "1 - 2 Bedrooms",
    beds_3: "3 - 4 Bedrooms",
    beds_5: "5+ Bedrooms",
    budget_q: "What is your estimated budget?",
    budget_1: "Under €1M",
    budget_2: "€1M - €3M",
    budget_3: "Over €3M",
    contact_sell: "Excellent. Our team specializes in maximizing property value. Shall we arrange a consultation?",
    contact_look: "Feel free to explore our curated collection. We are here when you are ready.",
    searching: "Curating a selection for you...",
    found: "We found some exceptional properties that match your criteria:",
    not_found: "We have exclusive off-market properties that match this criteria. Please contact us for details.",
    contact_us: "Contact Us",
    start_over: "Start Over",
    view_prop: "View details",
    price_on_request: "Price on request",
  },
  fr: {
    title: "Assistant DEM",
    hello: "Bienvenue chez DEM Properties. Comment pouvons-nous vous aider ?",
    buy: "Acheter un bien",
    sell: "Vendre un bien",
    look: "Je regarde simplement",
    type_q: "Quel type de bien recherchez-vous ?",
    villa: "Villa",
    apartment: "Appartement",
    penthouse: "Penthouse",
    location_q: "Quel secteur préférez-vous ?",
    loc_marbella: "Marbella & Environs",
    loc_estepona: "Estepona",
    loc_other: "Peu importe",
    beds_q: "De combien de chambres avez-vous besoin ?",
    beds_1: "1 - 2 Chambres",
    beds_3: "3 - 4 Chambres",
    beds_5: "5+ Chambres",
    budget_q: "Quel est votre budget estimé ?",
    budget_1: "Moins de 1M€",
    budget_2: "1M€ - 3M€",
    budget_3: "Plus de 3M€",
    contact_sell: "Excellent. Notre équipe est experte pour valoriser votre bien. Souhaitez-vous nous contacter ?",
    contact_look: "N'hésitez pas à explorer notre collection. Nous sommes à votre disposition.",
    searching: "Sélection des meilleures offres...",
    found: "Voici des propriétés exceptionnelles qui pourraient vous intéresser :",
    not_found: "Nous avons des biens exclusifs off-market correspondant à vos critères. Contactez-nous pour en savoir plus.",
    contact_us: "Nous contacter",
    start_over: "Recommencer",
    view_prop: "Voir les détails",
    price_on_request: "Prix sur demande",
  },
  es: {
    title: "Asistente DEM",
    hello: "Bienvenido a DEM Properties. ¿Cómo podemos ayudarle hoy?",
    buy: "Comprar una propiedad",
    sell: "Vender una propiedad",
    look: "Solo estoy mirando",
    type_q: "¿Qué tipo de propiedad busca?",
    villa: "Villa",
    apartment: "Apartamento",
    penthouse: "Ático",
    location_q: "¿Qué zona prefiere?",
    loc_marbella: "Marbella y alrededores",
    loc_estepona: "Estepona",
    loc_other: "Cualquier zona",
    beds_q: "¿Cuántas habitaciones necesita?",
    beds_1: "1 - 2 Habitaciones",
    beds_3: "3 - 4 Habitaciones",
    beds_5: "5+ Habitaciones",
    budget_q: "¿Cuál es su presupuesto estimado?",
    budget_1: "Menos de 1M€",
    budget_2: "1M€ - 3M€",
    budget_3: "Más de 3M€",
    contact_sell: "Excelente. Nuestro equipo es experto en maximizar el valor de su propiedad. ¿Desea contactarnos?",
    contact_look: "Siéntase libre de explorar nuestra colección. Estamos aquí cuando nos necesite.",
    searching: "Seleccionando las mejores opciones para usted...",
    found: "Hemos encontrado algunas propiedades excepcionales:",
    not_found: "Disponemos de propiedades exclusivas off-market que coinciden con su búsqueda. Contáctenos para más detalles.",
    contact_us: "Contáctenos",
    start_over: "Empezar de nuevo",
    view_prop: "Ver detalles",
    price_on_request: "Precio a consultar",
  }
};

interface Message {
  id: string;
  sender: "bot" | "user";
  text: string;
  options?: { label: string; action: () => void }[];
  properties?: Property[];
}

interface SearchCriteria {
  type: string;
  location: string;
  beds: number;
  budget: number;
}

const CHAT_PRICE_RANGES = [
  { min: 0, max: 1000000 },
  { min: 1000000, max: 3000000 },
  { min: 3000000, max: Number.MAX_SAFE_INTEGER },
];

const SmartChatbot: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language?.split("-")[0] || "en";
  const apiLang = lang === "fr" ? "es" : lang;
  const currentDict = dict[lang] || dict["en"];

  const { properties, loading } = useProperties(apiLang);

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  
  // Search Criteria State
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const criteriaRef = useRef<SearchCriteria>({ type: "", location: "", beds: 0, budget: 0 });
  const latestDataRef = useRef<{ loading: boolean; properties: Property[] }>({ loading: true, properties: [] });

  const normalizedProperties = useMemo(
    () =>
      (properties || []).filter((p) => Number(p.cod_ofer) > 0),
    [properties]
  );

  const getPropertyLocation = (p: Property) =>
    String(p.ciudad || p.poblacion || "").trim();

  const getPropertyType = (p: Property) =>
    String(p.nbtipo || "").trim();

  useEffect(() => {
    latestDataRef.current = { loading, properties: normalizedProperties };
  }, [loading, normalizedProperties]);

  const availableTypes = useMemo(() => {
    const counts = normalizedProperties.reduce<Record<string, number>>((acc, p) => {
      const value = getPropertyType(p);
      if (!value) return acc;
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([value]) => value);
  }, [normalizedProperties]);

  const availableLocations = useMemo(() => {
    const counts = normalizedProperties.reduce<Record<string, number>>((acc, p) => {
      const value = getPropertyLocation(p);
      if (!value) return acc;
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {});

    const dynamic = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([value]) => ({ key: value, label: value }));

    return [...dynamic, { key: "all", label: currentDict.loc_other }];
  }, [normalizedProperties, currentDict.loc_other]);

  // Auto-scroll
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping]);

  // Init Chat
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      startFlow();
    }
  }, [isOpen]);

  const addBotMessage = (msg: Omit<Message, "id" | "sender">, delay = 600) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages((prev) => [...prev, { ...msg, id: Date.now().toString(), sender: "bot" }]);
      setIsTyping(false);
    }, delay);
  };

  const addUserMessage = (text: string) => {
    setMessages((prev) => [...prev, { id: Date.now().toString(), sender: "user", text }]);
  };

  const startFlow = () => {
    const initialCriteria = { type: "", location: "", beds: 0, budget: 0 };
    criteriaRef.current = initialCriteria;
    setMessages([]);
    addBotMessage({
      text: currentDict.hello,
      options: [
        { label: currentDict.buy, action: () => handleBuyInit() },
        { label: currentDict.sell, action: () => handleSellInit() },
        { label: currentDict.look, action: () => handleLookInit() },
      ]
    }, 400);
  };

  const handleBuyInit = () => {
    addUserMessage(currentDict.buy);
    addBotMessage({
      text: currentDict.type_q,
      options:
        availableTypes.length > 0
          ? availableTypes.map((typeValue) => ({
              label: typeValue,
              action: () => handleType(typeValue, typeValue),
            }))
          : [
              { label: currentDict.villa, action: () => handleType("villa", currentDict.villa) },
              { label: currentDict.apartment, action: () => handleType("apartment", currentDict.apartment) },
              { label: currentDict.penthouse, action: () => handleType("penthouse", currentDict.penthouse) },
            ],
    });
  };

  const handleSellInit = () => {
    addUserMessage(currentDict.sell);
    addBotMessage({
      text: currentDict.contact_sell,
      options: [
        { label: currentDict.contact_us, action: () => { setIsOpen(false); navigate("/contact"); } },
        { label: currentDict.start_over, action: startFlow },
      ]
    });
  };

  const handleLookInit = () => {
    addUserMessage(currentDict.look);
    addBotMessage({
      text: currentDict.contact_look,
      options: [
        { label: currentDict.start_over, action: startFlow },
      ]
    });
  };

  const handleType = (type: string, label: string) => {
    const nextCriteria = { ...criteriaRef.current, type };
    criteriaRef.current = nextCriteria;
    
    addUserMessage(label);
    
    addBotMessage({
      text: currentDict.location_q,
      options: availableLocations.map((locationOpt) => ({
        label: locationOpt.label,
        action: () => handleLocation(locationOpt.key, locationOpt.label),
      })),
    });
  };

  const handleLocation = (location: string, label: string) => {
    const nextCriteria = { ...criteriaRef.current, location };
    criteriaRef.current = nextCriteria;
    addUserMessage(label);
    
    addBotMessage({
      text: currentDict.beds_q,
      options: [
        { label: currentDict.beds_1, action: () => handleBeds(1, currentDict.beds_1) },
        { label: currentDict.beds_3, action: () => handleBeds(3, currentDict.beds_3) },
        { label: currentDict.beds_5, action: () => handleBeds(5, currentDict.beds_5) },
      ]
    });
  };

  const handleBeds = (beds: number, label: string) => {
    const nextCriteria = { ...criteriaRef.current, beds };
    criteriaRef.current = nextCriteria;
    addUserMessage(label);
    
    addBotMessage({
      text: currentDict.budget_q,
      options: [
        { label: currentDict.budget_1, action: () => handleBudget(1, currentDict.budget_1) },
        { label: currentDict.budget_2, action: () => handleBudget(2, currentDict.budget_2) },
        { label: currentDict.budget_3, action: () => handleBudget(3, currentDict.budget_3) },
      ]
    });
  };

  const handleBudget = (budgetLevel: number, label: string) => {
    const nextCriteria = { ...criteriaRef.current, budget: budgetLevel };
    criteriaRef.current = nextCriteria;
    addUserMessage(label);
    
    // Show typing, then searching, then results
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, { id: Date.now().toString(), sender: "bot", text: currentDict.searching }]);
      
      // Perform search
      setTimeout(() => performSearch(nextCriteria), 1200);
    }, 500);
  };

  const normalizeText = (value: string | undefined | null) =>
    (value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const toNumber = (value: unknown): number => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value !== "string") return 0;
    const clean = value.replace(/[^\d,.-]/g, "").replace(/\.(?=\d{3}\b)/g, "").replace(",", ".");
    const parsed = Number(clean);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const performSearch = (searchCriteria: SearchCriteria, attempt = 0) => {
    setIsTyping(false);

    const { type, location, beds, budget: budgetLevel } = searchCriteria;
    const { loading: isLoading, properties: liveProperties } = latestDataRef.current;

    if (liveProperties.length === 0 && isLoading && attempt < 4) {
      setIsTyping(true);
      setTimeout(() => performSearch(searchCriteria, attempt + 1), 700);
      return;
    }

    let filtered = [...liveProperties];
    
    // Filter by type (same strategy as Properties page, exact value)
    if (type && type !== "all") {
      filtered = filtered.filter((p) => normalizeText(getPropertyType(p)) === normalizeText(type));
    }

    // Filter by location (same strategy as Properties page, exact value)
    if (location && location !== "all") {
      filtered = filtered.filter((p) => normalizeText(getPropertyLocation(p)) === normalizeText(location));
    }

    // Filter by beds
    if (beds === 1) {
      filtered = filtered.filter((p) => Number(p.total_hab || 0) >= 1 && Number(p.total_hab || 0) <= 2);
    } else if (beds === 3) {
      filtered = filtered.filter((p) => Number(p.total_hab || 0) >= 3 && Number(p.total_hab || 0) <= 4);
    } else if (beds === 5) {
      filtered = filtered.filter((p) => Number(p.total_hab || 0) >= 5);
    }

    // Filter by budget
    const beforeBudget = filtered;
    const priceRange = CHAT_PRICE_RANGES[budgetLevel - 1];
    if (priceRange) {
      filtered = filtered.filter((p) => {
        const price = toNumber(p.precioinmo);
        return price > 0 && price >= priceRange.min && price <= priceRange.max;
      });
    }

    // If price is missing on most listings, keep previous results instead of showing nothing.
    if (filtered.length === 0 && beforeBudget.length > 0) {
      filtered = beforeBudget;
    }

    // Relaxation path: if strict filters return nothing, progressively relax
    // (budget -> beds -> location) to still provide relevant suggestions.
    if (filtered.length === 0) {
      const withTypeOnly = [...liveProperties].filter((p) => {
        if (!type || type === "all") return true;
        return normalizeText(getPropertyType(p)) === normalizeText(type);
      });

      const withTypeLocation =
        location && location !== "all"
          ? withTypeOnly.filter(
              (p) => normalizeText(getPropertyLocation(p)) === normalizeText(location)
            )
          : withTypeOnly;

      filtered = withTypeLocation.length > 0 ? withTypeLocation : withTypeOnly;
    }

    // Sort best matches first: closest bedroom/price to selected criteria.
    const budgetTarget = budgetLevel === 1 ? 750000 : budgetLevel === 2 ? 2000000 : 4500000;
    filtered.sort((a, b) => {
      const bedsA = Number(a.total_hab || 0);
      const bedsB = Number(b.total_hab || 0);
      const priceA = toNumber(a.precioinmo);
      const priceB = toNumber(b.precioinmo);

      const scoreA = Math.abs(bedsA - beds) + Math.abs(priceA - budgetTarget) / 1000000;
      const scoreB = Math.abs(bedsB - beds) + Math.abs(priceB - budgetTarget) / 1000000;
      return scoreA - scoreB;
    });

    // Take top 2
    const results = filtered.slice(0, 2);

    if (results.length > 0) {
      setMessages(prev => [
        ...prev.filter(m => m.text !== currentDict.searching), // remove "searching" message
        {
          id: Date.now().toString(),
          sender: "bot",
          text: currentDict.found,
          properties: results,
          options: [
            { label: currentDict.start_over, action: startFlow },
          ]
        }
      ]);
    } else {
      setMessages(prev => [
        ...prev.filter(m => m.text !== currentDict.searching),
        {
          id: Date.now().toString(),
          sender: "bot",
          text: currentDict.not_found,
          options: [
            { label: currentDict.contact_us, action: () => { setIsOpen(false); navigate("/contact"); } },
            { label: currentDict.start_over, action: startFlow },
          ]
        }
      ]);
    }
  };

  return (
    <>
      {/* ================= FLOATING BUTTON ================= */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            aria-label="Open Assistant"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(true)}
            className="
              fixed bottom-24 right-6
              w-14 h-14 bg-stone-900 text-cream
              rounded-full shadow-xl
              flex items-center justify-center
              hover:bg-[#a5694f] transition-colors duration-300
              z-[90]
            "
          >
            <MessageSquare className="w-6 h-6" />
            
            {/* Pulse effect */}
            <span className="absolute inset-0 rounded-full bg-stone-900 animate-ping opacity-20" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* ================= CHAT WINDOW ================= */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95, transformOrigin: "bottom right" }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="
              fixed bottom-24 right-6
              w-[calc(100vw-3rem)] sm:w-[380px]
              h-[550px] max-h-[80vh]
              bg-white rounded-3xl
              shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)]
              border border-stone-100
              flex flex-col overflow-hidden
              z-[95]
            "
          >
            {/* Header */}
            <div className="bg-stone-900 px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <h3 className="font-oswald font-light tracking-widest text-cream uppercase text-sm">
                  {currentDict.title}
                </h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-stone-400 hover:text-white transition-colors"
                aria-label="Close Assistant"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5 bg-stone-50/50 scrollbar-hide">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex flex-col ${msg.sender === "user" ? "items-end" : "items-start"}`}
                >
                  <div
                    className={`
                      max-w-[85%] px-5 py-3.5 text-sm font-sans font-light leading-relaxed
                      ${msg.sender === "user" 
                        ? "bg-[#a5694f] text-white rounded-2xl rounded-tr-sm shadow-sm" 
                        : "bg-white text-stone-700 border border-stone-100 rounded-2xl rounded-tl-sm shadow-sm"}
                    `}
                  >
                    {msg.text}
                  </div>
                  
                  {/* Property Results */}
                  {msg.properties && msg.properties.length > 0 && (
                    <div className="mt-3 w-[280px] flex flex-col gap-3">
                      {msg.properties.map(p => (
                        <div key={p.cod_ofer} className="bg-white border border-stone-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                          <div className="h-32 bg-stone-200 relative">
                            {p.foto ? (
                              <img src={p.foto} alt={p.nbtipo} className="w-full h-full object-cover" />
                            ) : (
                              <div className="absolute inset-0 flex flex-col items-center justify-center bg-stone-50">
                                <span className="font-playfair italic text-lg text-stone-300 mb-1">D</span>
                                <span className="font-oswald text-[7px] uppercase tracking-[0.2em] text-stone-400">DEM PROPERTIES</span>
                              </div>
                            )}
                            <div className="absolute bottom-2 left-2 bg-stone-900/80 backdrop-blur-sm text-white px-2 py-1 text-[10px] uppercase font-oswald rounded">
                              {p.precioinmo > 0 ? `€${p.precioinmo.toLocaleString()}` : currentDict.price_on_request}
                            </div>
                          </div>
                          <div className="p-3">
                            <h4 className="font-oswald text-sm text-stone-800 uppercase tracking-wide truncate">{p.nbtipo} in {p.poblacion}</h4>
                            <div className="flex gap-3 text-xs text-stone-500 mt-2 font-sans">
                              <span>{p.total_hab} beds</span>
                              <span>{p.banyos} baths</span>
                            </div>
                            <button 
                              onClick={() => { setIsOpen(false); navigate(`/properties/${p.cod_ofer}`); }}
                              className="mt-3 w-full py-2 text-xs font-oswald tracking-widest uppercase text-[#a5694f] bg-[#a5694f]/5 hover:bg-[#a5694f]/10 rounded transition-colors"
                            >
                              {currentDict.view_prop}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Options */}
                  {msg.options && msg.options.length > 0 && (
                    <div className="flex flex-col gap-2 mt-3 w-full">
                      {msg.options.map((opt, i) => (
                        <button
                          key={i}
                          onClick={opt.action}
                          className="
                            w-full flex items-center justify-between
                            bg-white border border-stone-200 hover:border-[#a5694f]
                            text-stone-700 hover:text-[#a5694f]
                            px-4 py-3 rounded-xl
                            text-sm font-sans font-medium
                            transition-all shadow-sm hover:shadow
                          "
                        >
                          {opt.label}
                          <ChevronRight className="w-4 h-4 opacity-50" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <div className="flex items-start">
                  <div className="bg-white border border-stone-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex gap-1">
                    <span className="w-1.5 h-1.5 bg-stone-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-stone-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-stone-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Footer (No input, just brand) */}
            <div className="bg-white border-t border-stone-100 p-4 text-center">
              <span className="text-[10px] font-oswald uppercase tracking-widest text-stone-400">
                Powered by DEM Intelligence
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SmartChatbot;